import { PlainTooltip, IconButton } from "@similarweb/ui-components";

import type { IKeywordGroup, ServicesType } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { DeleteKeywordListConfirmationModal } from "app/components/modals/components/DeleteKeywordListConfirmationModal";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { useKeywordGroupModalWorkflow } from "app/pages/keyword-analysis/useKeywordGroupModalWorkflow";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { EDIT_TOOLTIP } from "../constants";
import { ButtonContainer } from "../KeywordsQueryBarStyles";
import { allTrackers } from "app/services/track/track";

export const EditGroupButton = ({
    services,
    keywordGroup,
    showGoToMonitorKeywordListButton = false,
    shouldConfirmDeleteKeywordList = false,
    onEditKeywordList,
}: {
    services: ServicesType;
    keywordGroup?: IKeywordGroup;
    showGoToMonitorKeywordListButton?: boolean;
    shouldConfirmDeleteKeywordList?: boolean;
    onEditKeywordList?: (modifiedGroup: IUserKeywordGroup) => void;
}) => {
    const translate = useTranslation();
    const editTooltip = translate(EDIT_TOOLTIP);

    const { controller, handleBeforeDelete, deleteConfirmationModalProps } = useKeywordGroupModalWorkflow({
        onDeleteSuccess: (deletedGroup) => {
            allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Delete");
            if (deletedGroup.Id === keywordGroup.id) {
                const homeState = services.swNavigator.current().homeState;
                void services.swNavigator.go(homeState);
            }
        },
    });

    const handleEditClick = () => {
        if (!keywordGroup) {
            return;
        }

        const keywordsGroup = {} as IUserKeywordGroup;
        keywordsGroup.Id = keywordGroup.id;
        keywordsGroup.Keywords = keywordGroup.keywords;
        keywordsGroup.Name = keywordGroup.text;

        controller.openSession({
            keywordsGroup,
        });
        allTrackers.dispatchTrackEvent("Button", "click", "Edit Keyword Group/Edit Icon");
    };

    const onDelete = (deletedGroup: IUserKeywordGroup) => {
        if (deletedGroup.Id === keywordGroup.id) {
            const homeState = services.swNavigator.current().homeState;
            void services.swNavigator.go(homeState);
        }
    };

    return (
        <PlainTooltip
            placement="bottom"
            tooltipContent={editTooltip}
        >
            <ButtonContainer>
                <IconButton
                    type="flat"
                    iconName="edit-icon"
                    aria-label={editTooltip}
                    dataAutomation="edit-group-button-edit-button"
                    onClick={handleEditClick}
                />
                <KeywordsGroupEditorModal
                    showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
                    onClose={() => controller.hide()}
                    onDelete={shouldConfirmDeleteKeywordList ? undefined : onDelete}
                    onBeforeDelete={shouldConfirmDeleteKeywordList ? handleBeforeDelete : undefined}
                    editTargetState={"organicsearch_keywordAnalysis_overview_2"}
                    onEdit={onEditKeywordList}
                    controller={controller}
                />
                {shouldConfirmDeleteKeywordList ? (
                    <DeleteKeywordListConfirmationModal
                        {...deleteConfirmationModalProps}
                        dataAutomationPrefix="edit-group-button-delete-keyword-list-confirmation"
                    />
                ) : null}
            </ButtonContainer>
        </PlainTooltip>
    );
};
